/**
 * useEntitlement
 * --------------
 * Resolves the current user's role + plan + entitlement status.
 *
 * Two sources, in priority order:
 *   1. `GET /services/jamcam/me` — authoritative, always fresh from DDB.
 *   2. Cognito ID-token claims (`custom:role`, `custom:plan`,
 *      `custom:entitlementStatus`) populated by the pre-token-generation
 *      trigger — used as an instant fallback while `/me` is loading so we
 *      don't flash an upgrade prompt.
 *
 * We can't rely on the ID token alone: it's cached in storage until Cognito
 * refreshes it, so a user who just upgraded (or finished checkout and was
 * redirected straight back to the app) still holds a stale token with
 * `plan=free` for up to an hour. `/me` reflects the write the Stripe webhook
 * already made to DDB.
 *
 * Anonymous users (not signed in) get the safe defaults:
 *   role='user', plan='free', entitlementStatus='free'.
 */

import { useEffect, useState } from 'react';
import { useApi, useUser } from '@charlava-com/auth';
import type {
  EntitlementStatus,
  JamCamMeResponse,
  Plan,
} from '@charlava-com/jamcam-svc';
import { useDevOverride } from './DevOverrideContext';

export type Role = 'admin' | 'user';

export interface EntitlementInfo {
  /** Claims have been resolved (loaded or determined to be anonymous). */
  ready: boolean;
  isAuthenticated: boolean;
  role: Role;
  plan: Plan;
  entitlementStatus: EntitlementStatus;
  /** True if the caller's status meets or exceeds `min`. Admins always pass. */
  can: (min: EntitlementStatus) => boolean;
  /** Convenience flags */
  isAdmin: boolean;
  isPaid: boolean;
  isTrial: boolean;
  isBlocked: boolean;
}

const ENTITLEMENT_RANK: Record<EntitlementStatus, number> = {
  blocked: -1,
  free: 0,
  // `trial`, `grace`, and `paid` all grant full Pro access. Rank them equally
  // so that a gate with `required="paid"` is satisfied while the user is
  // trialing or in the post-cancellation grace period.
  grace: 3,
  trial: 3,
  paid: 3,
};

const ANON_DEFAULT: Omit<EntitlementInfo, 'can'> = {
  ready: false,
  isAuthenticated: false,
  role: 'user',
  plan: 'free',
  entitlementStatus: 'free',
  isAdmin: false,
  isPaid: false,
  isTrial: false,
  isBlocked: false,
};

function buildCan(status: EntitlementStatus, role: Role) {
  return (min: EntitlementStatus): boolean => {
    if (role === 'admin') return true;
    return ENTITLEMENT_RANK[status] >= ENTITLEMENT_RANK[min];
  };
}

function buildInfo(
  role: Role,
  plan: Plan,
  entitlementStatus: EntitlementStatus
): EntitlementInfo {
  return {
    ready: true,
    isAuthenticated: true,
    role,
    plan,
    entitlementStatus,
    isAdmin: role === 'admin',
    isPaid: entitlementStatus === 'paid',
    isTrial: entitlementStatus === 'trial',
    isBlocked: entitlementStatus === 'blocked',
    can: buildCan(entitlementStatus, role),
  };
}

export function useEntitlement(): EntitlementInfo {
  const [userState, userActions] = useUser();
  const api = useApi();
  const devOverride = useDevOverride();
  const [info, setInfo] = useState<EntitlementInfo>(() => ({
    ...ANON_DEFAULT,
    can: buildCan('free', 'user'),
  }));

  useEffect(() => {
    let cancelled = false;

    if (userState.isLoading) return;

    // Anonymous — return defaults, mark ready.
    if (!userState.data) {
      setInfo({
        ...ANON_DEFAULT,
        ready: true,
        can: buildCan('free', 'user'),
      });
      return;
    }

    // 1) Seed from ID-token claims so there's no flash of "free".
    void userActions.getDecodedIdToken().then((claims) => {
      if (cancelled || !claims) return;
      const role = (
        claims['custom:role'] === 'admin' ? 'admin' : 'user'
      ) as Role;
      const plan = (claims['custom:plan'] === 'pro' ? 'pro' : 'free') as Plan;
      const rawStatus = claims['custom:entitlementStatus'];
      const entitlementStatus: EntitlementStatus =
        rawStatus === 'paid' ||
        rawStatus === 'trial' ||
        rawStatus === 'grace' ||
        rawStatus === 'blocked'
          ? rawStatus
          : 'free';
      setInfo(buildInfo(role, plan, entitlementStatus));
    });

    // 2) Fetch authoritative /me and let it override stale claims.
    void api
      .get('/services/jamcam/me')
      .then((res: unknown) => {
        if (cancelled) return;
        const me =
          (res as { data?: JamCamMeResponse })?.data ??
          (res as JamCamMeResponse);
        if (!me || typeof me !== 'object') return;
        // Preserve admin role from the token (it's derived from email, not /me).
        setInfo((prev) =>
          buildInfo(
            prev.role,
            me.plan ?? prev.plan,
            me.entitlementStatus ?? prev.entitlementStatus
          )
        );
      })
      .catch((err) => {
        // Non-fatal: we'll keep whatever the token claims provided.
        console.warn('useEntitlement: /me fetch failed', err);
      });

    return () => {
      cancelled = true;
    };
    // userActions and api are stable across renders; userState.data identity
    // changes on sign-in/out, which is exactly when we want to re-resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.data, userState.isLoading]);

  // Dev override: on localhost with bypass active, return synthetic max
  // privilege so every entitlement gate opens. Ignored in hosted envs.
  if (devOverride.isLocalhost && devOverride.bypassEntitlements) {
    return {
      ready: true,
      isAuthenticated: true,
      role: 'admin',
      plan: 'pro',
      entitlementStatus: 'paid',
      isAdmin: true,
      isPaid: true,
      isTrial: false,
      isBlocked: false,
      can: () => true,
    };
  }

  return info;
}
